import { useEffect, useState } from "react";
import { Mail, MessageCircle, Copy, ExternalLink, Loader2, CheckCircle2, XCircle, Send } from "lucide-react";
import { conceptOutreach, logOutreach, updateOutreach } from "@/lib/api";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

/**
 * Ready-to-send outreach messages (email + WhatsApp, links pre-filled) plus an
 * action tracker: channel + date/time + by + reply Yes/No + action taken + notes.
 */
export default function OutreachPanel({ lead, onOutreach }) {
  const [tpl, setTpl] = useState(null);
  const [entries, setEntries] = useState(lead.outreach || []);
  const [busy, setBusy] = useState("");

  useEffect(() => { setEntries(lead.outreach || []); }, [lead.lead_key]);

  useEffect(() => {
    if (!lead.concept_id) return;
    conceptOutreach(lead.concept_id, window.location.origin)
      .then(setTpl)
      .catch(() => setTpl(null));
  }, [lead.concept_id]);

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  const openEmail = () => {
    const to = tpl?.to_email || lead.email || "";
    const url = `mailto:${to}?subject=${encodeURIComponent(tpl.email_subject)}&body=${encodeURIComponent(tpl.email_body)}`;
    window.open(url, "_blank");
  };

  const openWhatsApp = () => {
    const num = (tpl?.to_whatsapp || lead.whatsapp || "").replace(/[^\d]/g, "");
    const url = `https://wa.me/${num}?text=${encodeURIComponent(tpl.whatsapp_text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const markSent = async (channel) => {
    setBusy(channel);
    try {
      const entry = await logOutreach({ lead_key: lead.lead_key, channel });
      const next = [...entries, entry];
      setEntries(next);
      onOutreach?.(next);
      toast.success(`${channel === "email" ? "Email" : "WhatsApp"} logged as sent`);
    } catch { toast.error("Could not log outreach"); } finally { setBusy(""); }
  };

  const patchEntry = async (entry, patch) => {
    const next = entries.map((e) => (e.id === entry.id ? { ...e, ...patch } : e));
    setEntries(next); onOutreach?.(next);
    try { await updateOutreach({ lead_key: lead.lead_key, entry_id: entry.id, ...patch }); }
    catch { toast.error("Could not update tracker"); }
  };

  if (!lead.concept_id) return null;

  return (
    <div data-testid="outreach-panel">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Ready-to-send message</p>

      {!tpl ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-3"><Loader2 size={14} className="animate-spin" /> Preparing message…</div>
      ) : (
        <>
          {/* Email block */}
          <div className="border border-slate-200 rounded-xl overflow-hidden mb-3">
            <div className="flex items-center justify-between bg-slate-50 px-3 py-2">
              <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Mail size={13} /> Email</span>
              <div className="flex items-center gap-1.5">
                <button onClick={() => copy(`${tpl.email_subject}\n\n${tpl.email_body}`, "Email")} data-testid="outreach-copy-email" className="text-[11px] font-semibold text-brand hover:underline flex items-center gap-1"><Copy size={12} /> Copy</button>
                <button onClick={openEmail} data-testid="outreach-open-email" className="text-[11px] font-semibold text-slate-500 hover:text-brand flex items-center gap-1"><ExternalLink size={12} /> Open</button>
              </div>
            </div>
            <div className="p-3">
              <p className="text-xs font-semibold text-slate-700">{tpl.email_subject}</p>
              <pre className="text-[11px] text-slate-500 whitespace-pre-wrap font-sans mt-1 max-h-28 overflow-y-auto">{tpl.email_body}</pre>
              <button onClick={() => markSent("email")} disabled={busy === "email"} data-testid="outreach-mark-email-sent" className="btn-primary rounded-lg px-3 py-1.5 text-xs mt-2 flex items-center gap-1.5 disabled:opacity-50">
                {busy === "email" ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Mark email sent
              </button>
            </div>
          </div>

          {/* WhatsApp block */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between bg-green-50 px-3 py-2">
              <span className="text-xs font-semibold text-green-700 flex items-center gap-1.5"><MessageCircle size={13} /> WhatsApp</span>
              <div className="flex items-center gap-1.5">
                <button onClick={() => copy(tpl.whatsapp_text, "WhatsApp message")} data-testid="outreach-copy-whatsapp" className="text-[11px] font-semibold text-green-700 hover:underline flex items-center gap-1"><Copy size={12} /> Copy</button>
                <button onClick={openWhatsApp} data-testid="outreach-open-whatsapp" className="text-[11px] font-semibold text-slate-500 hover:text-green-700 flex items-center gap-1"><ExternalLink size={12} /> Open</button>
              </div>
            </div>
            <div className="p-3">
              <pre className="text-[11px] text-slate-500 whitespace-pre-wrap font-sans max-h-28 overflow-y-auto">{tpl.whatsapp_text}</pre>
              <button onClick={() => markSent("whatsapp")} disabled={busy === "whatsapp"} data-testid="outreach-mark-whatsapp-sent" className="bg-green-600 text-white rounded-lg px-3 py-1.5 text-xs mt-2 flex items-center gap-1.5 hover:bg-green-700 disabled:opacity-50">
                {busy === "whatsapp" ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Mark WhatsApp sent
              </button>
            </div>
          </div>
        </>
      )}

      {/* Action tracker */}
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mt-5 mb-2">Action tracker</p>
      {entries.length === 0 ? (
        <p className="text-xs text-slate-400">No outreach logged yet.</p>
      ) : (
        <div className="space-y-2" data-testid="outreach-tracker">
          {entries.map((e) => <TrackerRow key={e.id} entry={e} onPatch={(p) => patchEntry(e, p)} />)}
        </div>
      )}
    </div>
  );
}

function TrackerRow({ entry, onPatch }) {
  const [action, setAction] = useState(entry.action_taken || "");
  const [notes, setNotes] = useState(entry.notes || "");
  const dirty = action !== (entry.action_taken || "") || notes !== (entry.notes || "");
  const Icon = entry.channel === "email" ? Mail : MessageCircle;
  return (
    <div className="bg-slate-50 rounded-lg p-3 text-sm" data-testid="outreach-entry">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 capitalize"><Icon size={13} /> {entry.channel} sent</span>
        <span className="text-[10px] text-slate-400">{fmt(entry.sent_at)} · {entry.by}</span>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <span className="text-[11px] text-slate-500">Reply received?</span>
        <button onClick={() => onPatch({ reply_received: true })} data-testid="outreach-reply-yes"
          className={`text-[11px] font-semibold rounded px-2 py-0.5 flex items-center gap-1 border ${entry.reply_received === true ? "bg-green-50 border-green-300 text-green-700" : "border-slate-200 text-slate-400"}`}><CheckCircle2 size={11} /> Yes</button>
        <button onClick={() => onPatch({ reply_received: false })} data-testid="outreach-reply-no"
          className={`text-[11px] font-semibold rounded px-2 py-0.5 flex items-center gap-1 border ${entry.reply_received === false ? "bg-slate-200 border-slate-300 text-slate-600" : "border-slate-200 text-slate-400"}`}><XCircle size={11} /> No</button>
      </div>
      <input value={action} onChange={(e) => setAction(e.target.value)} placeholder="Action taken…" data-testid="outreach-action-input"
        className="w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-xs mt-2 outline-none focus:border-brand" />
      <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes…" data-testid="outreach-notes-input"
        className="w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-xs mt-1.5 outline-none focus:border-brand" />
      {dirty && (
        <button onClick={() => onPatch({ action_taken: action, notes })} data-testid="outreach-entry-save"
          className="btn-primary rounded-md px-3 py-1 text-[11px] mt-2">Save</button>
      )}
    </div>
  );
}
